import React, { useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  SellersContainer,
  SellerShowContainer,
  SellerDemographicContainer,
  SellerAssetsContainer,
  SellerContactsContainer,
  SellerAllAssetsContainer,
  AssetsTabButtonContainer,
  AssetsTabButton,
  AssetsTabText,
  TabButtonContainer,
  TabButton,
} from './elements';
import {
  MainContainer,
  BankAccountsContainer
} from '../../elements';
import SubNav from './SubNav';
import { Text, ScrollView, View } from 'react-native';
import { SELLER } from './queries';
import { useQuery } from '@apollo/client';
import DataState from '../DataState';
import AssetCard from '../../elements/AssetCard';
import Contacts from '../Contacts';
import SellerAssets from '../Assets/SellerAssets';
import CardIndex from '../Users/SellerUsers/CardIndex';
import { elLightGray, elRed } from '../../styles/colors';
import { formatAddress } from '../../utils/formatAssetName';
import { currency } from 'app/src/utils/stringHelpers';
import AuthContext from 'app/src/contexts/AuthContext';
import { Button } from 'app/src/elements/buttons';
import { Warning, WarningText } from 'app/src/styles';
import Modal from 'app/src/elements/Modal';
import moment from 'moment';
import EntityAttachments from '../Attachments/EntityAttachments';
import BankAccounts from '../BankAccounts';
import secrets from '../../../secrets';

import FeeMarkups from './FeeMarkups';
import Review from './Review';

const ReviewDetails = ({ seller }) => {
  if (!seller.reviewedAt) { return null; }

  const action = 'Active' === seller.status ? 'Approved' : seller.status;

  return (
    <View>
      <Text>{action} by {seller.reviewedBy?.fullName}</Text>
      <Text>
        {moment(seller.reviewedAt).format('MM-DD-YY \@ hh:mmA')}
      </Text>
      <Text>Review Notes: {seller.reviewNotes}</Text>
    </View>
  );
};

const FILE_TYPES = [
  {
    name: "W9",
    required: false,
  },
  {
    name: "Other",
    required: false
  }
];

const Seller = () => {
  const { sellerId } = useParams();
  const [seller, setSeller] = useState();
  const [contacts, setContacts] = useState([]);
  const [sellerUsers, setSellerUsers] = useState([]);
  const [currentTab, setCurrentTab] = useState('Contacts')
  const [activeAssetsTab, setActiveAssetsTab] = useState('Unsold');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const { hasPolicy, currentUser } = useContext(AuthContext);

  const defaultFileIds = {}
  FILE_TYPES.forEach(f => defaultFileIds[f.name] = []);
  const [fileIds, setFileIds] = useState(defaultFileIds);

  const { loading, error, data, refetch } = useQuery(
    SELLER, { variables: { id: sellerId } }
  );

  useEffect(() => {
    if(data?.seller) {
      setSeller(data.seller);
      if(!contacts.length) {
        setContacts(data.seller.contacts);
      }
      if(!sellerUsers.length) {
        setSellerUsers(data.seller.sellerUsers);
      }

      let keys = Object.keys(fileIds);
      let newFileIds = fileIds;
      keys.map(key => {
        newFileIds[key] = data.seller.files.filter(
          file => file.name === key
        );
      })
      setFileIds(newFileIds);
    }
  }, [data])

  if(loading || !seller) { return <DataState.Loading />; }

  let sellerFee = 'No Fee Setup';

  if (seller?.feeAmount > 0) {
    if ('Percent' === seller.feeType) {
      sellerFee = `Percent - ${seller.feeAmount}%`;
    } else {
      sellerFee = `Flat Fee - ${currency(seller.feeAmount)}`;
    }
  }

  let statusInformation = null;

  if ('New' === seller.status) {
    statusInformation = (
      <Warning>
        <WarningText>
          This seller was created via the website. The user is awaiting
          approval from ELAG.
        </WarningText>
        {currentUser?.canApproveSeller && (
          <>
            <Button
              label="Confirm"
              style={{ width: 300, backgroundColor: 'green' }}
              onPress={() => setShowReviewModal('Approve')}
            />
            <Button
              label="Decline"
              style={{ width: 300 }}
              onPress={() => setShowReviewModal('Decline')}
            />
          </>
        )}
      </Warning>
    );
  } else if ('Approved' === seller.status) {
    statusInformation = (
      <Warning>
        <WarningText>
          {
            `This Seller has been Approved. ` +
            `An Email has Been Sent to the Seller to Confirm Their Account.`
          }
        </WarningText>
        <ReviewDetails seller={seller} />
      </Warning>
    );
  } else if ('Declined' === seller.status) {
    statusInformation = (
      <Warning>
        <WarningText>
          This seller was previously declined access.
        </WarningText>
        <ReviewDetails seller={seller} />
        {currentUser?.canApproveSeller && (
          <Button
            label="Change to Approved"
            onPress={() => setShowReviewModal('Approve')}
          />
        )}
      </Warning>
    );
  }

  return (
    <MainContainer>
      <SubNav
        seller={{
          id: sellerId,
          name: seller?.name,
          companyName: seller?.companyName,
          displayName: seller?.displayName,
          street: seller?.street,
          city: seller?.city,
          state: seller?.state,
          postalCode: seller?.postalCode?.formatted,
          phoneNumber: seller?.phoneNumber?.formatted,
          contacts: seller?.contacts,
          primaryContactId: seller?.primaryContactId,
          feeType: seller?.feeType,
          feeAmount: seller?.feeAmount,
          inspectionFeeAmount: seller?.inspectionFeeAmount,
          releaseOption: seller?.releaseOption,
          isEquiplinc: seller?.isEquiplinc,
          canAddAsset: seller?.canAddAsset,
          showDatePaid: seller?.showDatePaid,
        }}
        assignmentNotifications={seller?.assignmentNotifications}
        refetch={refetch}
        setUpdatedContactDetails={setUpdatedContactDetails}
      />
      <SellersContainer>
        {statusInformation}
        <SellerShowContainer>
          <SellerDemographicContainer>
            <Text>
              {seller?.name}
            </Text>
            {seller?.companyName && (
              <Text>
                {seller.companyName}
              </Text>
            )}
            <Text>
              {formatAddress(seller, 'Seller')}
            </Text>
            <br />
            <Text>
              <b>Total Number of Assets all Time: </b>
              {seller?.allTimeAssetCount}
            </Text>
            <br />
            <Text>
              <b>Current Assets: </b>
              {seller?.assetCount}
            </Text>
            <br />
            <Text>
              <b>Assets Sold Last 90 Days: </b>
              {seller?.assetsSold90DayCount}
            </Text>
            <br />
            <Text>
              <b>Seller Fee: </b>
              {sellerFee}
            </Text>
            <br />
            {seller?.inspectionFeeAmount && seller.inspectionFeeAmount > 0 ?
              <Text>
                <b>Inspection Fee: </b>
                {currency(seller.inspectionFeeAmount)}
              </Text> :
              null
            }
            <FeeMarkups seller={seller} refetch={refetch} />
            {secrets.app.doubleEntry &&
              <EntityAttachments
                ownerType={'Seller'}
                ownerId={seller?.id}
                fileTypes={FILE_TYPES}
                fileIds={fileIds}
                setFileIds={setFileIds}
              />
            }
          </SellerDemographicContainer>
          <SellerAssetsContainer>
            <Text><b>Assets in Upcoming Sales</b></Text>
            <br />
            {getNextAssets()}
          </SellerAssetsContainer>
          <SellerContactsContainer>
            <TabButtonContainer>
              <TabButton
                onPress={() => setCurrentTab('Contacts')}
                style={{backgroundColor: currentTab === 'Contacts' ? elRed : elLightGray}}
              >
                <Text>Contacts</Text>
              </TabButton>
              {hasPolicy({ type: 'SellerUser', action: 'index' }) && (
                <TabButton
                  onPress={() => setCurrentTab('SellerUsers')}
                  style={{
                    backgroundColor:
                    currentTab === 'SellerUsers' ? elRed : elLightGray
                  }}
                >
                  <Text>Seller Users</Text>
                </TabButton>
              )}
            </TabButtonContainer>
            {getActiveTabbedContent()}
          </SellerContactsContainer>
        </SellerShowContainer>
        <AssetsTabButtonContainer>
          <AssetsTabButton
            onPress={() => setActiveAssetsTab('Unsold')}
            active={'Unsold' === activeAssetsTab}
          >
            <AssetsTabText active={'Unsold' === activeAssetsTab}>
              Unsold Assets
            </AssetsTabText>
          </AssetsTabButton>
          <AssetsTabButton
            onPress={() => setActiveAssetsTab('Sold')}
            active={'Sold' === activeAssetsTab}
          >
            <AssetsTabText active={'Sold' === activeAssetsTab}>
              Sold Assets
            </AssetsTabText>
          </AssetsTabButton>
          <AssetsTabButton
            onPress={() => setActiveAssetsTab('Removed')}
            active={'Removed' === activeAssetsTab}
          >
            <AssetsTabText active={'Removed' === activeAssetsTab}>
              Removed Assets
            </AssetsTabText>
          </AssetsTabButton>
        </AssetsTabButtonContainer>
        <SellerAllAssetsContainer>
          <SellerAssets
            withNav={false}
            sellerId={sellerId}
            assetStatus={activeAssetsTab.toLowerCase()}
          />
        </SellerAllAssetsContainer>
        { secrets.app.doubleEntry &&
          <BankAccountsContainer>
            <BankAccounts ownerType='Seller' ownerId={seller.id} />
          </BankAccountsContainer>
        }
        {showReviewModal &&
          <Modal
            showModal={showReviewModal}
            setShowModal={setShowReviewModal}
            modalType={`${showReviewModal} ${seller.name}`}
          >
            <Review
              type={showReviewModal}
              seller={seller}
              refetch={refetch}
              setShowModal={setShowReviewModal}
            />
          </Modal>
        }
      </SellersContainer>
    </MainContainer>
  )

  function setUpdatedContactDetails(contactDetails, newContact, hiddenContact) {
    let newContacts = contacts;
    if(newContact) {
      newContacts = [...newContacts, contactDetails]
      newContacts = newContacts.map(contact => {
        if(parseInt(contact.id) === parseInt(contactDetails.id) &&
          contactDetails.primary
        ) {
          return contact;
        }else {
          return {...contact, primary: false}
        }
      })
    }else if(hiddenContact) {
      newContacts = newContacts.filter(contact => {
        return contact.id !== contactDetails.id
      })
    }else {
      newContacts = newContacts.map(contact => {
        if(parseInt(contact.id) === parseInt(contactDetails.id)) {
          return contactDetails;
        }
        if(contactDetails.primary) {
          return {...contact, primary: false}
        }else {
          return contact;
        }
      });
    }

    if(contactDetails.primary) {
      newContacts = newContacts.sort((a, b) => b.primary - a.primary);
    }
    setContacts(newContacts);
  }

  function setUpdatedSellerUserDetails(sellerUserDetails, newSellerUser, hiddenSellerUser) {
    let newSellerUsers = sellerUsers;
    if(newSellerUser) {
      newSellerUsers = [...newSellerUsers, sellerUserDetails]
    }else if(hiddenSellerUser) {
      newSellerUsers = newSellerUsers.filter(sellerUser => {
        return sellerUser.id !== sellerUserDetails.id
      })
    }else {
      newSellerUsers = newSellerUsers.map(sellerUser => {
        if(parseInt(sellerUser.id) === parseInt(sellerUserDetails.id)) {
          return sellerUserDetails;
        }
        return sellerUser;
      });
    }
    setSellerUsers(newSellerUsers);
  }

  function getNextAssets() {
    return (
      <ScrollView>
        {seller?.upcomingSaleAssets?.map(saleAsset => (
          <AssetCard
            saleAsset={saleAsset}
            key={saleAsset.id}
          />
        ))}
      </ScrollView>
    )
  }

  function getActiveTabbedContent() {
    if(!seller) return <DataState.Loading />
    if(currentTab === 'Contacts') {
      return <Contacts
        contacts={contacts}
        sellerId={sellerId}
        setUpdatedContactDetails={setUpdatedContactDetails}
      />
    }else {
      return <CardIndex
        sellerUsers={sellerUsers}
        sellerId={sellerId}
        setUpdatedSellerUserDetails={setUpdatedSellerUserDetails}
      />
    }
  }
}

export default Seller;
